import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Workflow } from 'src/app/components/settings/workflows/workflow.models';
import { GUEST_SESSION_HEADER, getGuestSessionToken } from './guest-session';

export interface WorkflowRequest {
  name: string;
  sku: string;
  referenceCode: string;
  description: string | null;
  tier: string;
  stageCount: number;
  activeStage: number;
  definition: any;
  lastValidation: any;
  inFlightCount: number;
  concurrencyToken: string | null;
}

export interface WorkflowAuditLogEntry {
  auditLogId: string;
  workflowTemplateId: number;
  entityType: string;
  entityId: string;
  action: string;
  userId: number | null;
  userEmail: string | null;
  timestampUtc: string;
  sessionId: string | null;
  ipAddress: string | null;
  beforeSnapshot: any;
  afterSnapshot: any;
  changeDiff: any;
  reason: string | null;
}

export interface WorkflowResponse {
  workflowTemplateId: number;
  workflowVersionId: number;
  companyId: number;
  name: string;
  sku: string;
  referenceCode: string;
  description: string | null;
  tier: string;
  version: number;
  status: string;
  stageCount: number;
  activeStage: number;
  definition: any;
  auditLog: any[];
  lastValidation: any;
  inFlightCount: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  deactivatedAt: string | null;
  deactivationReason: string | null;
}

export type WorkflowGroupType = 'USER_GROUP' | 'TEMP_USER_GROUP';

export interface WorkflowGroupSchedule {
  isScheduled: boolean;
  recurrenceFrequencyUnit?: string | null;
  frequencyDay?: string | null;
  selectedDaysOfTheWeek?: string[] | null;
  specificDay?: string | null;
  recurrenceOnSpecificDay?: boolean | null;
  firstOrLastDay?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  overdueInterval?: number | null;
  overdueUnit?: string | null;
}

export interface WorkflowAssignmentGroup {
  groupId: number;
  groupType: WorkflowGroupType;
  groupName?: string | null;
  schedule?: WorkflowGroupSchedule | null;
}

export interface CreateWorkflowAssignmentRequest {
  accountHolderCompanyId: number;
  clientPlantId: number;
  groups: WorkflowAssignmentGroup[];
}

export interface WorkflowAssignmentResponse {
  workflowAssignmentId: number;
  workflowTemplateId: number;
  accountHolderCompanyId: number;
  accountHolderCompanyName: string | null;
  clientPlantId: number;
  clientPlantName: string | null;
  groups: WorkflowAssignmentGroup[];
  dateCreated: string;
}

export type WorkflowUserType = 'USER' | 'TEMP_USER';

export interface AssignmentRoleUser {
  userId: number | null;
  userSubjectId?: string | null;
  userType: WorkflowUserType;
}

export interface RoleUsers {
  workflowActorRoleId: number;
  users: AssignmentRoleUser[];
}

export interface WorkflowSummary {
  workflowTemplateId: number;
  workflowVersionId: number;
  name: string;
  sku: string;
  referenceCode: string;
  description: string | null;
  tier: string;
  version: number;
  status: string;
  stageCount: number;
  inFlightCount: number;
  updatedAt: string;
  publishedAt: string | null;
  deactivatedAt: string | null;
}

export interface WorkflowRuntimeState {
  workflowItemId: number;
  status: string;
  currentStageNumber: number;
  currentStageStatus: string | null;
}

export interface AvailableWorkflow {
  workflowTemplateId: number;
  workflowVersionId: number;
  name: string;
  description: string | null;
  version: number;
  stageCount: number;
  status: string;
  runtimeState: WorkflowRuntimeState | null;
  actionLabel: string;
  canAct?: boolean;
  canSubmit?: boolean;
  canApprove?: boolean;
  canReturn?: boolean;
  canReject?: boolean;
  canResubmit?: boolean;
  canView?: boolean;
  // RT-13: a saved (un-submitted) Stage 1 draft exists for this version → "Resume".
  hasDraft?: boolean;
  // Scheduling: a due occurrence exists for this initiator's group that they haven't started.
  isScheduled?: boolean;
  dueAt?: string | null;
  isOverdue?: boolean;
  pendingOccurrenceId?: number | null;
}

export interface WorkflowDraft {
  workflowTemplateId: number;
  workflowVersionId: number;
  payload: any;
  updatedAt: string;
}

export interface WorkflowRuntimeListItem {
  workflowItemId: number;
  workflowTemplateId: number;
  workflowVersionId: number;
  name: string;
  status: string;
  currentActorRole: string | null;
  lastAction: string | null;
  statusLabel?: string;
  derivedStatusLabel?: string;
  actionLabel: string;
  currentStageNumber: number;
  stageCount?: number;
  initiatorName?: string | null;
  lastReturnReason?: string | null;
  summaryTitle?: string | null;
  summaryFields?: { label: string; value: string }[];
  startedAt: string;
  submittedAt?: string | null;
  updatedAt: string;
  completedAt: string | null;
  rejectedAt?: string | null;
  // RT-21: current-stage SLA — entry time, computed deadline, and overdue flag.
  stageEnteredAt?: string | null;
  slaDeadline?: string | null;
  isOverdue?: boolean;
  canView: boolean;
  canAct: boolean;
  canSubmit: boolean;
  canApprove: boolean;
  canReturn: boolean;
  canReject: boolean;
  canResubmit: boolean;
}

export interface MyWorkResponse {
  needsMyAction: WorkflowRuntimeListItem[];
  availableToStart: AvailableWorkflow[];
}

export interface WorkflowRuntimeActionRequest {
  reason?: string;
  payload?: any;
}

export interface WorkflowRuntimeDefinition {
  workflowTemplateId: number;
  workflowVersionId: number;
  name: string;
  description: string | null;
  version: number;
  stageCount: number;
  initialStageNumber: number;
  definition: any;
}

export interface WorkflowRuntimeStage {
  stageNumber: number;
  status: string;
}

export interface WorkflowRuntimeInstance {
  workflowItemId: number;
  workflowTemplateId: number;
  workflowVersionId: number;
  name: string;
  description: string | null;
  version: number;
  stageCount: number;
  status: string;
  currentStageNumber: number;
  currentActorRole?: string | null;
  lastAction?: string | null;
  lastReturnReason?: string | null;
  definition: any;
  payload?: any;
  startedAt: string;
  updatedAt: string;
  completedAt: string | null;
  rejectedAt?: string | null;
  // RT-21: current-stage SLA — entry time, computed deadline, and overdue flag.
  stageEnteredAt?: string | null;
  slaDeadline?: string | null;
  isOverdue?: boolean;
  // RT-17: set when the item has been escalated after an SLA breach.
  isEscalated?: boolean;
  escalationRole?: string | null;
  canAct?: boolean;
  canSubmit?: boolean;
  canApprove?: boolean;
  canReturn?: boolean;
  canReject?: boolean;
  canResubmit?: boolean;
  canView?: boolean;
  outputs?: WorkflowRuntimeOutput[];
}

export interface WorkflowRuntimeOutput {
  workflowOutputId: number;
  format: string;
  status: string;
  fileName?: string | null;
  downloadable: boolean;
}

export interface WorkflowOutputRegenerateResult {
  workflowItemId: number;
  accepted: boolean;
  flaggedCount: number;
  message: string;
}

export interface WorkflowStagePersistenceResponse {
  instance: WorkflowRuntimeInstance;
  currentStage: WorkflowRuntimeStage;
  nextStage: WorkflowRuntimeStage | null;
}

export interface WorkflowLinkRequest {
  workflowItemId: number;
  targetActorRole?: string | null;
  recipientEmail?: string | null;
  expiryHours?: number | null;
}

export interface WorkflowIssuedLink {
  workflowAssignmentLinkId: number;
  workflowItemId: number;
  token: string;
  targetActorRole?: string | null;
  recipientEmail?: string | null;
  status: string;
  expiresAt: string;
  createdAt: string;
  /** Ready-to-paste URL; null when the environment has no link host configured. */
  url?: string | null;
}

export interface WorkflowLinkValidation {
  isValid: boolean;
  status: string;
  message: string;
  workflowItemId?: number | null;
  targetActorRole?: string | null;
  requiresOtp: boolean;
  recipientEmailMasked?: string | null;
}

export interface WorkflowGuestOtpResult {
  sent: boolean;
  message: string;
  emailMasked?: string | null;
  expiresAt?: string | null;
  /** Present when a resend was throttled (HTTP 429). */
  retryAfterSeconds?: number | null;
  /** Seconds until another code may be requested — drives the resend countdown. */
  resendAvailableInSeconds?: number | null;
}

export interface WorkflowGuestSession {
  verified: boolean;
  message: string;
  workflowItemId?: number | null;
  guestSessionToken?: string | null;
  remainingAttempts?: number | null;
  expiresAt?: string | null;
  targetActorRole?: string | null;
  email?: string | null;
}

export interface WorkflowRuntimeAuditEvent {
  auditLogId: string;
  workflowItemId: number;
  action: string;
  stageNumber: number | null;
  userId: number;
  userEmail: string | null;
  timestampUtc: string;
  previousStatus: string | null;
  newStatus: string | null;
  details: any;
}

export interface WorkflowNotification {
  workflowNotificationId: number;
  workflowItemId: number;
  eventType: string;
  channel: string;
  status: string;
  isRead: boolean;
  createdAt: string;
  sentAt: string | null;
  workflowName: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class WorkflowService {
  private baseUrl = `${environment.apiUrl}/workflows`;

  constructor(private http: HttpClient) {}

  getAll(status?: string): Observable<WorkflowSummary[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.http.get<WorkflowSummary[]>(`${this.baseUrl}${query}`);
  }

  getMyWork(): Observable<MyWorkResponse> {
    return this.http.get<MyWorkResponse>(`${this.baseUrl}/runtime/my-work`);
  }

  getWorkflowHistory(): Observable<WorkflowRuntimeListItem[]> {
    return this.http.get<WorkflowRuntimeListItem[]>(`${this.baseUrl}/runtime/history`);
  }

  /** Admin/Super-Admin: all workflow items (all stages) for an account holder + client. */
  getWorkflowsForScope(accountHolderCompanyId: number, clientPlantId: number): Observable<WorkflowRuntimeListItem[]> {
    return this.http.get<WorkflowRuntimeListItem[]>(
      `${this.baseUrl}/runtime/scope?accountHolderCompanyId=${accountHolderCompanyId}&clientPlantId=${clientPlantId}`
    );
  }

  getHistory(): Observable<WorkflowRuntimeListItem[]> {
    return this.getWorkflowHistory();
  }

  getWorkflowExceptions(): Observable<WorkflowRuntimeListItem[]> {
    return this.http.get<WorkflowRuntimeListItem[]>(`${this.baseUrl}/runtime/exceptions`);
  }

  retryRouting(workflowItemId: number): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/items/${workflowItemId}/retry-routing`,
      {}
    );
  }

  getRuntimeDefinition(workflowVersionId: number): Observable<WorkflowRuntimeDefinition> {
    return this.http.get<WorkflowRuntimeDefinition>(`${this.baseUrl}/runtime/definitions/${workflowVersionId}`);
  }

  getRuntimeItem(workflowItemId: number): Observable<WorkflowRuntimeInstance> {
    return this.http.get<WorkflowRuntimeInstance>(`${this.baseUrl}/runtime/items/${workflowItemId}`);
  }

  downloadRuntimeOutput(workflowOutputId: number): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.baseUrl}/runtime/outputs/${workflowOutputId}/download`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  /* ---- RT-22 in-app notifications ---- */
  getNotifications(unreadOnly = false): Observable<WorkflowNotification[]> {
    const query = unreadOnly ? '?unreadOnly=true' : '';
    return this.http.get<WorkflowNotification[]>(`${this.baseUrl}/runtime/notifications${query}`);
  }

  markNotificationRead(workflowNotificationId: number): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/runtime/notifications/${workflowNotificationId}/read`, {});
  }

  /* ---- RT-27 runtime audit trail for a single item ---- */
  getRuntimeAuditTrail(workflowItemId: number): Observable<WorkflowRuntimeAuditEvent[]> {
    return this.http.get<WorkflowRuntimeAuditEvent[]>(`${this.baseUrl}/runtime/items/${workflowItemId}/audit`);
  }

  /* ---- D3: issue a shareable link to this workflow item. The API builds the URL from
     the environment's configured host, so the console never hardcodes a domain. ---- */
  issueRuntimeLink(request: WorkflowLinkRequest): Observable<WorkflowIssuedLink> {
    return this.http.post<WorkflowIssuedLink>(`${this.baseUrl}/runtime/links`, request);
  }

  /* ---- D3: resolve an emailed link token to its workflow item. Answers 200 either way;
     `isValid` carries the verdict. ---- */
  validateWorkflowLink(token: string): Observable<WorkflowLinkValidation> {
    return this.http.get<WorkflowLinkValidation>(
      `${this.baseUrl}/runtime/links/${encodeURIComponent(token)}/validate`
    );
  }

  /* ---- Guest access: a link recipient with no account verifies by email OTP, then acts on
     the single item their link names. The session token is the guest's only credential. ---- */
  requestGuestOtp(token: string): Observable<WorkflowGuestOtpResult> {
    return this.http.post<WorkflowGuestOtpResult>(
      `${this.baseUrl}/runtime/guest/otp/request`, { token });
  }

  verifyGuestOtp(token: string, code: string): Observable<WorkflowGuestSession> {
    return this.http.post<WorkflowGuestSession>(
      `${this.baseUrl}/runtime/guest/otp/verify`, { token, code });
  }

  getGuestItem(): Observable<WorkflowRuntimeInstance> {
    return this.http.get<WorkflowRuntimeInstance>(
      `${this.baseUrl}/runtime/guest/item`, { headers: this.guestHeaders() });
  }

  guestApprove(request?: WorkflowRuntimeActionRequest): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/guest/approve`, this.toActionRequest(request),
      { headers: this.guestHeaders() });
  }

  guestReturn(request?: WorkflowRuntimeActionRequest): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/guest/return`, this.toActionRequest(request),
      { headers: this.guestHeaders() });
  }

  guestReject(request?: WorkflowRuntimeActionRequest): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/guest/reject`, this.toActionRequest(request),
      { headers: this.guestHeaders() });
  }

  guestResubmit(request?: WorkflowRuntimeActionRequest): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/guest/resubmit`, this.toActionRequest(request),
      { headers: this.guestHeaders() });
  }

  endGuestSession(): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/runtime/guest/session/end`, {},
      { headers: this.guestHeaders() });
  }

  private guestHeaders(): HttpHeaders {
    return new HttpHeaders({ [GUEST_SESSION_HEADER]: getGuestSessionToken() ?? '' });
  }

  /* ---- RT-32 append a note to a workflow item's activity history ---- */
  addRuntimeNote(workflowItemId: number, note: string): Observable<WorkflowRuntimeAuditEvent> {
    return this.http.post<WorkflowRuntimeAuditEvent>(
      `${this.baseUrl}/runtime/items/${workflowItemId}/note`, { note });
  }

  regenerateRuntimeOutputs(workflowItemId: number): Observable<WorkflowOutputRegenerateResult> {
    return this.http.post<WorkflowOutputRegenerateResult>(
      `${this.baseUrl}/runtime/items/${workflowItemId}/outputs/regenerate`, {});
  }

  getRuntimeInstance(workflowItemId: number): Observable<WorkflowRuntimeInstance> {
    return this.getRuntimeItem(workflowItemId);
  }

  submitWorkflow(workflowVersionId: number, payload?: any, scheduledOccurrenceId?: number | null): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/definitions/${workflowVersionId}/submit`,
      { payload: payload !== undefined ? this.clean(payload) : null, scheduledOccurrenceId: scheduledOccurrenceId ?? null }
    );
  }

  /* ---- D3: provision a blank Stage 1 item ahead of submission, so a link can be shared
     before anyone has filled anything in. Idempotent — reissues the same item on repeat calls. ---- */
  startPendingItem(workflowVersionId: number, scheduledOccurrenceId?: number | null): Observable<WorkflowRuntimeInstance> {
    return this.http.post<WorkflowRuntimeInstance>(
      `${this.baseUrl}/runtime/definitions/${workflowVersionId}/start`,
      { scheduledOccurrenceId: scheduledOccurrenceId ?? null }
    );
  }

  submitPendingItem(workflowItemId: number, payload?: any): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/items/${workflowItemId}/submit`,
      { payload: payload !== undefined ? this.clean(payload) : null }
    );
  }

  guestSubmitPendingItem(request?: WorkflowRuntimeActionRequest): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/guest/submit`, this.toActionRequest(request),
      { headers: this.guestHeaders() });
  }

  approveWorkflow(workflowItemId: number, request?: WorkflowRuntimeActionRequest): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/items/${workflowItemId}/approve`,
      this.toActionRequest(request)
    );
  }

  returnWorkflow(workflowItemId: number, request?: WorkflowRuntimeActionRequest): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/items/${workflowItemId}/return`,
      this.toActionRequest(request)
    );
  }

  rejectWorkflow(workflowItemId: number, request?: WorkflowRuntimeActionRequest): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/items/${workflowItemId}/reject`,
      this.toActionRequest(request)
    );
  }

  resubmitWorkflow(workflowItemId: number, payload?: any): Observable<WorkflowStagePersistenceResponse> {
    return this.http.post<WorkflowStagePersistenceResponse>(
      `${this.baseUrl}/runtime/items/${workflowItemId}/resubmit`,
      { payload: payload !== undefined ? this.clean(payload) : null }
    );
  }

  /* ---- RT-13 Save Draft (runtime Stage 1 work-in-progress) ---- */
  saveRuntimeDraft(workflowVersionId: number, payload?: any): Observable<WorkflowDraft> {
    return this.http.post<WorkflowDraft>(
      `${this.baseUrl}/runtime/definitions/${workflowVersionId}/draft`,
      { payload: payload !== undefined ? this.clean(payload) : null }
    );
  }

  getRuntimeDraft(workflowVersionId: number): Observable<WorkflowDraft> {
    return this.http.get<WorkflowDraft>(`${this.baseUrl}/runtime/definitions/${workflowVersionId}/draft`);
  }

  deleteRuntimeDraft(workflowVersionId: number): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/runtime/definitions/${workflowVersionId}/draft`);
  }

  getById(id: number, version?: number): Observable<WorkflowResponse> {
    const query = version ? `?version=${version}` : '';
    return this.http.get<WorkflowResponse>(`${this.baseUrl}/${id}${query}`);
  }

  getBySku(sku: string, version?: number): Observable<WorkflowResponse> {
    const query = version ? `?version=${version}` : '';
    return this.http.get<WorkflowResponse>(`${this.baseUrl}/sku/${encodeURIComponent(sku)}${query}`);
  }

  create(workflow: Workflow): Observable<WorkflowResponse> {
    return this.http.post<WorkflowResponse>(this.baseUrl, this.toRequest(workflow));
  }

  saveDraft(id: number, workflow: Workflow, concurrencyToken?: string | null): Observable<WorkflowResponse> {
    return this.http.put<WorkflowResponse>(`${this.baseUrl}/${id}/draft`, this.toRequest(workflow, concurrencyToken));
  }

  publish(id: number, workflow: Workflow, concurrencyToken?: string | null): Observable<WorkflowResponse> {
    return this.http.post<WorkflowResponse>(`${this.baseUrl}/${id}/publish`, {
      lastValidation: this.clean(workflow.lastValidation),
      concurrencyToken: concurrencyToken || null,
    });
  }

  deactivate(id: number, reason?: string, concurrencyToken?: string | null, acknowledge = false): Observable<WorkflowResponse> {
    // RT-30: `acknowledge` confirms the admin has seen the in-flight-items warning. Without it the
    // API returns 409 (requiresAcknowledgement + inFlightCount) when items are still in flight.
    return this.http.post<WorkflowResponse>(`${this.baseUrl}/${id}/deactivate`, { reason, concurrencyToken: concurrencyToken || null, acknowledge });
  }

  /* ---- Tenant assignments (account holder + client + groups) ---- */
  getAssignments(workflowTemplateId: number): Observable<WorkflowAssignmentResponse[]> {
    return this.http.get<WorkflowAssignmentResponse[]>(`${this.baseUrl}/${workflowTemplateId}/assignments`);
  }

  createAssignment(
    workflowTemplateId: number,
    request: CreateWorkflowAssignmentRequest
  ): Observable<WorkflowAssignmentResponse> {
    return this.http.post<WorkflowAssignmentResponse>(
      `${this.baseUrl}/${workflowTemplateId}/assignments`,
      request
    );
  }

  deleteAssignment(workflowTemplateId: number, assignmentId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${workflowTemplateId}/assignments/${assignmentId}`);
  }

  /* ---- Role → user assignments (scoped to a tenant assignment) ---- */
  getRoleUsers(workflowTemplateId: number, assignmentId: number): Observable<RoleUsers[]> {
    return this.http.get<RoleUsers[]>(
      `${this.baseUrl}/${workflowTemplateId}/assignments/${assignmentId}/role-users`
    );
  }

  setRoleUsers(
    workflowTemplateId: number,
    assignmentId: number,
    roleId: number,
    users: AssignmentRoleUser[]
  ): Observable<AssignmentRoleUser[]> {
    return this.http.put<AssignmentRoleUser[]>(
      `${this.baseUrl}/${workflowTemplateId}/assignments/${assignmentId}/role-users/${roleId}`,
      { users }
    );
  }

  getAuditLog(
    id: number,
    filters?: { action?: string; user?: string; from?: string; to?: string }
  ): Observable<WorkflowAuditLogEntry[]> {
    let params: string[] = [];
    if (filters?.action) params.push(`action=${encodeURIComponent(filters.action)}`);
    if (filters?.user) params.push(`user=${encodeURIComponent(filters.user)}`);
    if (filters?.from) params.push(`from=${encodeURIComponent(filters.from)}`);
    if (filters?.to) params.push(`to=${encodeURIComponent(filters.to)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return this.http.get<WorkflowAuditLogEntry[]>(`${this.baseUrl}/${id}/audit${qs}`);
  }

  /** Check whether a recipient role resolves to any users. */
  previewRecipients(role: string): Observable<{ role: string; userCount: number; resolvable: boolean }> {
    return this.http.get<{ role: string; userCount: number; resolvable: boolean }>(
      `${this.baseUrl}/recipients/preview?role=${encodeURIComponent(role)}`
    );
  }

  /** Probe whether the notification template service is reachable. */
  notificationTemplatesHealth(): Observable<{ available: boolean }> {
    return this.http.get<{ available: boolean }>(`${this.baseUrl}/notification-templates/health`);
  }

  recordAuditExport(id: number, payload: { format: string; count: number; filter: string }): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/${id}/audit/export`, payload);
  }

  private toRequest(workflow: Workflow, concurrencyToken?: string | null): WorkflowRequest {
    return {
      name: workflow.name,
      sku: workflow.sku,
      referenceCode: (workflow as Workflow & { referenceCode?: string }).referenceCode || '0000',
      description: (workflow as Workflow & { description?: string }).description || null,
      tier: workflow.tier,
      stageCount: workflow.stageCount,
      activeStage: workflow.activeStage,
      definition: this.clean({
        stages: workflow.stages,
        finalOutputs: workflow.finalOutputs,
      }),
      lastValidation: this.clean(workflow.lastValidation),
      inFlightCount: workflow.inFlightCount,
      concurrencyToken: concurrencyToken || null,
    };
  }

  private clean(value: any): any {
    if (value instanceof Set) return Array.from(value);
    if (typeof value === 'function') return undefined;
    if (typeof value === 'number' && !Number.isFinite(value)) return 0;
    if (Array.isArray(value)) return value.map((item) => this.clean(item));
    if (value && typeof value === 'object') {
      return Object.keys(value).reduce((acc, key) => {
        const next = this.clean(value[key]);
        if (next !== undefined) acc[key] = next;
        return acc;
      }, {} as any);
    }
    return value;
  }

  private toActionRequest(request?: WorkflowRuntimeActionRequest): WorkflowRuntimeActionRequest {
    return {
      reason: request?.reason || undefined,
      payload: request?.payload !== undefined ? this.clean(request.payload) : null,
    };
  }
}
